// Reference asset paths for lineart + colored versions. Convention only — the DB
// does NOT store these URLs. If the directory layout ever moves (e.g. CDN), only
// this helper changes.
//
// Format split (see docs/asset-production-guide.md for the full reasoning):
//   - lineart → PNG (lossless, sharp edges, flood-fill friendly — JPG 8×8
//     DCT noise around line edges destroys flood fill purity)
//   - colored → JPG quality 85 (smaller, soft transitions tolerate lossy)
//
// Files live under /assets/packs/{packId}/{category}/{itemId}-{variant}[-thumb].{ext}
// and ship via Cloudflare Pages.
//
// User artworks (kid's coloring output) live in R2, not here — see r2.ts.

import { PACK_IDS, type PackId } from './packs.ts'

export type AssetVariant = 'lineart' | 'colored'
export type AssetCategory = 'items' | 'bonus'

export function assetExtension(variant: AssetVariant): 'png' | 'jpg' {
  return variant === 'lineart' ? 'png' : 'jpg'
}

export function assetUrl(
  packId: PackId,
  itemId: string,
  variant: AssetVariant,
  category: AssetCategory = 'items',
  thumb = false,
): string {
  const ext = assetExtension(variant)
  const suffix = thumb ? `-${variant}-thumb` : `-${variant}`
  return `/assets/packs/${packId}/${category}/${itemId}${suffix}.${ext}`
}

/**
 * Resolve a bonus scene asset URL from the snake-case bonus ID.
 *
 * Bonus IDs are the only snake-case identifier in the codebase: they encode
 * `{packId}_{sceneId}` so the pack prefix can be split off without colliding
 * with hyphens used inside item ids (`silver-cup`, `baby-jesus`). The scene
 * id portion lands in the same kebab namespace as items.
 *
 *   bonusAssetUrl('joseph_panorama', 'lineart')
 *     → /assets/packs/joseph/bonus/panorama-lineart.png
 */
export function bonusAssetUrl(bonusId: string, variant: AssetVariant, thumb = false): string {
  const packId = PACK_IDS.find((p) => bonusId.startsWith(`${p}_`))
  if (!packId) {
    throw new Error(`Unknown pack in bonus id: ${bonusId}`)
  }
  const sceneId = bonusId.slice(packId.length + 1)
  if (!sceneId) {
    throw new Error(`Empty scene id in bonus id: ${bonusId}`)
  }
  return assetUrl(packId, sceneId, variant, 'bonus', thumb)
}

/** Object key for a user's colored artwork in the R2 bucket. */
export function artworkR2Key(userId: string, artworkId: string): string {
  return `artworks/${userId}/${artworkId}.png`
}

/** Object key for a user artwork's thumbnail (300×164 JPG). */
export function artworkThumbnailR2Key(userId: string, artworkId: string): string {
  return `thumbnails/${userId}/${artworkId}.jpg`
}

/**
 * Object key for a share card PNG (1080×1080 canvas-composed).
 * Public resource keyed by share id, not user id — the share is the unit
 * of access, not the kid who made it.
 */
export function shareCardR2Key(shareId: string): string {
  return `share-cards/${shareId}.png`
}
